import type { Catalog } from '../types';

/**
 * Vocabulário compartilhado: ações, substantivos genéricos e o andaime de estados vazios.
 *
 * A chave nomeia o **significado**, nunca o texto em português — `common.action.save`, e não
 * `common.salvar`. Assim a copy em português pode ser reescrita sem renomear nada.
 */
export const common = {
  'common.notFound.title': { pt: 'Página não encontrada', en: 'Page not found' },
  'common.notFound.body': { pt: 'Não encontramos esta página.', en: 'We could not find this page.' },
  'common.backHome': { pt: 'Voltar ao início', en: 'Back to the start' },
  /** O `Placeholder` de tela ainda por construir — some quando a última fase entregar. */
  'common.comingInPhase': {
    pt: 'Esta tela chega na fase {phase} da implementação.',
    en: 'This screen arrives in phase {phase} of the build.',
  },
  'common.dismiss': { pt: 'Dispensar', en: 'Dismiss' },
  /**
   * A primeira entrada plural do catálogo. O mínimo entra como marcador em vez de ficar escrito
   * na frase, senão mudar `PASSWORD_MIN` deixaria as duas traduções mentindo em silêncio.
   */
  'common.passwordMissing': {
    pt: {
      one: 'Falta {count} caractere — mínimo {min}',
      other: 'Faltam {count} caracteres — mínimo {min}',
    },
    en: {
      one: '{count} character to go — minimum {min}',
      other: '{count} characters to go — minimum {min}',
    },
  },
  'common.passwordOk': { pt: 'Senha aceita', en: 'Password accepted' },
  'common.passwordGood': { pt: 'Boa senha', en: 'Good password' },

  /** Os nomes acessíveis dos botões de mais/menos, onde `{label}` é o que se conta. */
  'a11y.decrease': { pt: 'Diminuir {label}', en: 'Decrease {label}' },
  'a11y.increase': { pt: 'Aumentar {label}', en: 'Increase {label}' },

  'common.or': { pt: 'OU', en: 'OR' },
  'common.show': { pt: 'MOSTRAR', en: 'SHOW' },
  'common.hide': { pt: 'OCULTAR', en: 'HIDE' },
  'common.showPassword': { pt: 'Mostrar senha', en: 'Show password' },
  'common.hidePassword': { pt: 'Ocultar senha', en: 'Hide password' },
  /**
   * O status de **uma** volta. Não é o `rounds.filter.status.*`, que rotula o recorte da lista e
   * por isso está no plural — "Concluídas" no filtro, "Concluída" na volta.
   */
  'pill.status.completed': { pt: '✓ Concluída', en: '✓ Completed' },
  'pill.status.inProgress': { pt: 'Em andamento', en: 'In progress' },
  'pill.status.abandoned': { pt: 'Abandonada', en: 'Abandoned' },
  'pill.verified': { pt: 'Verificado', en: 'Verified' },
  'pill.verifiedTitle': { pt: 'Campo verificado', en: 'Verified course' },

  /**
   * O cartão e a barra de distribuição, compartilhados entre a volta, o campo e o registro.
   *
   * `OUT`, `IN` e `TOT` **não** entram no catálogo: são notação de cartão, iguais nos dois idiomas
   * e usados no código como identidade do segmento, não como rótulo. Mesmo critério do `E` de
   * *even*, e o mesmo que mantém *birdie*, *bogey* e *par* como estão em português.
   */
  'scorecard.hole': { pt: 'Buraco', en: 'Hole' },
  'scorecard.par': { pt: 'Par', en: 'Par' },
  /** *Stroke index* — a abreviação é a mesma nos dois idiomas. */
  'scorecard.si': { pt: 'SI', en: 'SI' },
  'scorecard.strokes': { pt: 'Tacadas', en: 'Strokes' },
  'scorecard.putts': { pt: 'Putts', en: 'Putts' },
  'scorecard.fw': { pt: 'FW', en: 'FW' },
  'scorecard.gir': { pt: 'GIR', en: 'GIR' },
  'scorecard.legend.birdie': { pt: 'Birdie', en: 'Birdie' },
  'scorecard.legend.bogey': { pt: 'Bogey', en: 'Bogey' },
  'scorecard.legend.double': { pt: 'Double+', en: 'Double+' },
  'scorecard.legend.par': { pt: 'Sem forma = par', en: 'No mark = par' },
  /** O rótulo da linha de distâncias quando o tee não tem nome — no cartão do campo. */
  'scorecard.distance': { pt: 'Distância', en: 'Distance' },

  'dist.birdies': { pt: 'Birdies+', en: 'Birdies+' },
  'dist.pars': { pt: 'Pars', en: 'Pars' },
  'dist.bogeys': { pt: 'Bogeys', en: 'Bogeys' },
  'dist.doubles': { pt: 'Doubles+', en: 'Doubles+' },

  /** O nome acessível da marca de score — a forma desenhada não carrega texto. */
  'score.notPlayed': { pt: 'não jogado', en: 'not played' },
  'score.shape.eagle': { pt: 'eagle ou melhor', en: 'eagle or better' },
  'score.shape.birdie': { pt: 'birdie', en: 'birdie' },
  'score.shape.par': { pt: 'par', en: 'par' },
  'score.shape.bogey': { pt: 'bogey', en: 'bogey' },
  'score.shape.double': { pt: 'double bogey ou pior', en: 'double bogey or worse' },

  'common.offline': {
    pt: 'Sem conexão — vamos tentar de novo sozinhos',
    en: 'No connection — we will keep retrying',
  },
} as const satisfies Catalog;
